// ===================================
// MAGIC LOGIN PAGE
// frontend/src/pages/MagicLogin.js
// Handles magic link authentication
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './MagicLogin.css';

const MagicLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Use setAuth for magic link (no API call needed, just store token)
  const { setAuth, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Process magic link on mount
  useEffect(() => {
    const verifyMagicLink = async () => {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token || !email) {
        setStatus('error');
        setError('Invalid magic link. Please request a new one.');
        return;
      }

      try {
        const response = await api.post('/auth/magic-login', { token, email });
        
        if (response.data.success) {
          setStatus('success');
          // Use setAuth (not login) - just store token and user, no API call
          setAuth(response.data.token, response.data.user);
          
          // Brief delay to show success, then redirect
          setTimeout(() => {
            navigate('/app/dashboard');
          }, 1500);
        }
      } catch (err) {
        setStatus('error');
        const errorData = err.response?.data;
        
        if (errorData?.expired) {
          setError('This link has expired. Please request a new one.');
        } else {
          setError(errorData?.message || 'Invalid or expired link');
        }
      }
    };

    verifyMagicLink();
  }, [searchParams, setAuth, navigate]);

  return (
    <div className="magic-login-page">
      <div className="magic-login-container">
        {/* Logo */}
        <div className="magic-login-logo">
          <Link to="/">
            <span className="logo-icon">🎯</span>
            <span className="logo-text">Career-Coach.AI</span>
          </Link>
        </div>

        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="magic-login-content">
            <div className="status-icon verifying">
              <span className="spinner-large"></span>
            </div>
            <h1>Signing you in...</h1>
            <p>Please wait while we verify your magic link.</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="magic-login-content">
            <div className="status-icon success">✓</div>
            <h1>Welcome back!</h1>
            <p>Redirecting to your dashboard...</p>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="magic-login-content">
            <div className="status-icon error">✗</div>
            <h1>Link Invalid</h1>
            <p className="error-message">{error}</p>
            
            <div className="magic-login-actions">
              <Link to="/login" className="btn btn-primary">
                Go to Login
              </Link>
              <Link to="/register" className="btn btn-secondary">
                Create Account
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLogin;